import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNextBestActions, type Recommendation } from '@/lib/hooks/use-next-best-actions';
import { useBasePath } from '@/lib/config/nav-helpers';
import { api } from '@/lib/api';
import { rejectAction } from '@/lib/actions';
import { SALES_TODAY_KEY, setSignalState } from '@/lib/sales-today';
import { planResolution } from './resolution-plan';
import { followUpHref } from '@/routes/hubs/command-center/widgets/FollowUpsWidget';
import { DataCard, QueryError, SkeletonList, useToast } from '../ui';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ActionFeedCardProps {
  escalations: any;
  followUps: any;
  isLoading: boolean;
  /** A failed load is not "All clear": with nothing to show it renders a retryable error instead. */
  isError?: boolean;
  onRetry?: () => void;
}

interface FeedItem {
  id: string;
  rawId: string;
  type: 'escalation' | 'followup' | 'opportunity';
  title: string;
  description: string;
  urgency: 'critical' | 'high' | 'medium' | 'low';
  urgencyScore: number;
  href: string | null;
  icon: string;
  timestamp?: string;
  confidence?: number;
  impactFormatted?: string;
  impactCents?: number;
  evidenceSources?: string[];
  causalNarrative?: string | null;
  optimalTiming?: string | null;
  category?: string;
  dealValue?: number;
  /** Sales-engine deal cards: the Deal Coach key + fingerprint their done/snooze state is stored under. */
  signalKey?: string | null;
  fingerprint?: string | null;
}

type TabKey = 'all' | 'urgent' | 'followups' | 'opportunities';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const MAX_VISIBLE = 10;

const URGENCY_DOT: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-amber-500',
  medium: 'bg-blue-400',
  low: 'bg-gray-300 dark:bg-gray-600',
};

const SEVERITY_TO_URGENCY: Record<string, FeedItem['urgency']> = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low',
  info: 'low',
};

const URGENCY_SCORE_MAP: Record<string, number> = {
  critical: 100,
  high: 75,
  medium: 50,
  low: 25,
};

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'urgent', label: 'Urgent' },
  { key: 'followups', label: 'Follow-ups' },
  { key: 'opportunities', label: 'Opportunities' },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function timeAgo(iso: string | undefined): string | null {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

function formatImpact(cents: number | undefined): string | null {
  if (!cents || cents < 100) return null;
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${Math.round(dollars / 1_000)}k`;
  return `$${Math.round(dollars)}`;
}

function buildInsight(item: FeedItem): string | null {
  const parts: string[] = [];

  if (item.causalNarrative && item.causalNarrative.length > 5) {
    parts.push(item.causalNarrative);
  }

  if (!parts.length && item.confidence != null) {
    const pct = Math.round(item.confidence * 100);
    const impact = formatImpact(item.impactCents);
    if (item.type === 'opportunity' && impact) {
      parts.push(`${pct}% confidence this action protects ${impact} in revenue.`);
    } else if (item.type === 'opportunity') {
      parts.push(`Model confidence: ${pct}%. Based on pattern analysis across your connected data sources.`);
    }
  }

  if (!parts.length && item.description) {
    const desc = item.description.replace(/Key drivers:\s*\.?\s*/, '').trim();
    if (desc.length > 10) return null;
  }

  if (item.optimalTiming) {
    parts.push(`Act ${item.optimalTiming.toLowerCase().startsWith('today') ? 'today' : item.optimalTiming.toLowerCase()}.`);
  }

  if (item.evidenceSources?.length) {
    parts.push(`Signals: ${item.evidenceSources.join(', ')}.`);
  }

  return parts.length ? parts.join(' ') : null;
}

/* ------------------------------------------------------------------ */
/*  Mapping helpers                                                    */
/* ------------------------------------------------------------------ */

function mapEscalations(actions: any[] | undefined, basePath: string): FeedItem[] {
  if (!actions?.length) return [];
  return actions.map((item) => {
    const urgency = SEVERITY_TO_URGENCY[item.severity] ?? 'low';
    return {
      id: `escalation-${item.id}`,
      rawId: item.id,
      type: 'escalation' as const,
      title: item.title,
      description: item.description,
      urgency,
      urgencyScore: URGENCY_SCORE_MAP[urgency] ?? 25,
      href: `${basePath}/actions?focus=${item.id}`,
      icon: '⚠️',
      timestamp: item.createdAt ?? item.created_at,
      dealValue: item.deal_value ?? item.dealValue,
    };
  });
}

function mapFollowUps(items: any[] | undefined, basePath: string): FeedItem[] {
  if (!items?.length) return [];
  return items.map((item) => ({
    id: `followup-${item.id}`,
    rawId: item.id,
    type: 'followup' as const,
    title: item.contactName ?? item.contact_name,
    description: item.detail ? `${item.reason} — ${item.detail}` : item.reason,
    urgency: (['critical', 'high', 'medium', 'low'].includes(item.urgency) ? item.urgency : 'low') as FeedItem['urgency'],
    urgencyScore: typeof item.urgencyScore === 'number' ? item.urgencyScore : typeof item.urgency_score === 'number' ? item.urgency_score : URGENCY_SCORE_MAP[item.urgency] ?? 25,
    // Land on the thing itself: the client page, else the deal on the pipeline, else the inbox row.
    href: followUpHref(basePath, { id: item.id, clientId: item.clientId ?? null, dealId: item.dealId ?? null }),
    icon: '\u{1F4CB}',
    timestamp: item.createdAt ?? item.created_at ?? item.dueAt ?? item.due_at,
    dealValue: item.relatedDealValue ? item.relatedDealValue * 100 : item.deal_value ?? item.dealValue,
    signalKey: item.signalKey ?? null,
    fingerprint: item.fingerprint ?? null,
  }));
}

function mapRecommendations(recs: Recommendation[] | undefined, basePath: string): FeedItem[] {
  if (!recs?.length) return [];
  return recs.map((rec) => {
    // Deep-link each recommendation to where it's actionable instead of the
    // generic Actions inbox (which reads a different, often-empty source — so a
    // churn re-engage card used to land on a "0 items" screen). Client/contact
    // targets open the client page; deals open the pipeline; invoices the
    // revenue view. Falls back to the inbox only when there's no entity target.
    const target = rec.targetEntities?.[0];
    // Every feed item must land on the EXACT thing it's about. Client/contact
    // targets open the rich client page; everything else (deals, invoices, or no
    // entity) opens the Action Inbox focused on this action — never a generic
    // pipeline/board scroll the user then has to hunt through.
    let href = `${basePath}/actions?focus=${rec.id}`;
    if (target?.id && (target.type === 'client' || target.type === 'contact')) {
      href = `${basePath}/clients/${encodeURIComponent(target.id)}`;
    }
    return {
      id: `opportunity-${rec.id}`,
      rawId: rec.id,
      type: 'opportunity' as const,
      title: rec.title,
      description: rec.description,
      urgency: rec.urgency,
      urgencyScore: rec.urgencyScore,
      href,
      icon: '\u{1F4A1}',
      confidence: rec.confidence ?? undefined,
      impactFormatted: rec.expectedImpactFormatted,
      impactCents: rec.expectedImpactCents,
      evidenceSources: rec.evidenceSources,
      causalNarrative: rec.causalNarrative,
      optimalTiming: rec.optimalTiming,
      category: rec.category,
    };
  });
}

/* ------------------------------------------------------------------ */
/*  Row                                                                */
/* ------------------------------------------------------------------ */

function FeedRow({
  item,
  onNavigate,
  expanded,
  onToggleExpand,
  onDismiss,
  onDone,
  dismissing,
}: {
  item: FeedItem;
  onNavigate: (path: string) => void;
  expanded: boolean;
  onToggleExpand: () => void;
  onDismiss: () => void;
  onDone: () => void;
  dismissing: boolean;
}) {
  const age = timeAgo(item.timestamp);
  const isCritical = item.urgency === 'critical';
  const impact = formatImpact(item.impactCents) ?? (item.dealValue ? formatImpact(item.dealValue) : null);
  const insight = expanded ? buildInsight(item) : null;

  return (
    <div
      className={`group transition-all duration-200 ${dismissing ? 'opacity-0 h-0 overflow-hidden' : ''}`}
    >
      {/* Primary row — single tight line. Clicking anywhere on it opens the detail; "Open" in the detail navigates. */}
      <div
        onClick={onToggleExpand}
        className="flex items-center gap-2 py-[5px] px-1 rounded-sm hover:bg-surface-1 transition-colors cursor-pointer"
      >
        {/* Urgency dot */}
        <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${URGENCY_DOT[item.urgency]}`} />
        {isCritical && (
          <span className="absolute -ml-0.5 w-1.5 h-1.5 rounded-full bg-red-500 animate-ping opacity-50" />
        )}

        {/* Title — the main content; a button so the keyboard can open the detail too */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
          aria-expanded={expanded}
          className="flex-1 min-w-0 text-left"
        >
          <span className="text-xs text-ink leading-tight line-clamp-1">
            {item.title}
          </span>
        </button>

        {/* Right side: impact + age + actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Impact pill — only show when significant */}
          {impact && (
            <span className="text-3xs font-medium tabular-nums text-emerald-600 dark:text-emerald-400">
              {impact}
            </span>
          )}

          {/* Confidence arc — tiny inline indicator */}
          {item.confidence != null && item.confidence >= 0.7 && (
            <span className="text-3xs tabular-nums text-ink-muted">
              {Math.round(item.confidence * 100)}%
            </span>
          )}

          {age && (
            <span className="text-3xs tabular-nums text-ink-muted w-4 text-right">
              {age}
            </span>
          )}

          {/* Hover actions */}
          <div className="flex items-center gap-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDone(); }}
              title="Done"
              className="rounded-sm p-0.5 text-ink-muted hover:text-emerald-500 transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3.5 8 6.5 11 12.5 5" />
              </svg>
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDismiss(); }}
              title="Dismiss"
              className="rounded-sm p-0.5 text-ink-muted hover:text-red-400 transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="4" y1="4" x2="12" y2="12" />
                <line x1="12" y1="4" x2="4" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Expanded insight — compact, real intelligence */}
      {expanded && (
        <div className="ml-4 mr-1 mb-1 px-2.5 py-2 rounded-md bg-surface-1 border border-border/40 space-y-1.5">
          {/* Description (cleaned up) */}
          <p className="text-2xs text-ink-dim leading-relaxed">
            {item.description.replace(/Key drivers:\s*\.?\s*/, '').trim() || item.description}
          </p>

          {/* AI insight */}
          {insight && (
            <p className="text-2xs text-ink leading-relaxed">
              {insight}
            </p>
          )}

          {/* Compact metadata row */}
          <div className="flex items-center justify-between gap-2 pt-0.5">
            <div className="flex items-center gap-2">
              {item.confidence != null && (
                <span className="text-3xs text-ink-muted">
                  {Math.round(item.confidence * 100)}% confidence
                </span>
              )}
              {item.evidenceSources && item.evidenceSources.length > 0 && (
                <span className="text-3xs text-ink-muted">
                  via {item.evidenceSources.join(', ')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {item.href && (
                <button
                  type="button"
                  onClick={() => onNavigate(item.href!)}
                  className="text-2xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  Open &rarr;
                </button>
              )}
              <button
                type="button"
                onClick={onDone}
                className="text-2xs font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-500 transition-colors"
              >
                Done
              </button>
              <button
                type="button"
                onClick={onDismiss}
                className="text-2xs font-medium text-ink-muted hover:text-red-400 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main                                                               */
/* ------------------------------------------------------------------ */

export function ActionFeedCard({ escalations, followUps, isLoading, isError, onRetry }: ActionFeedCardProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const queryClient = useQueryClient();
  const { data: nbaData, isLoading: nbaLoading } = useNextBestActions(5);
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const { toast } = useToast();

  const allItems = useMemo<FeedItem[]>(() => {
    const esc = mapEscalations(escalations?.actions, basePath);
    const fu = mapFollowUps(followUps?.items, basePath);
    const nba = mapRecommendations(nbaData?.result.recommendations, basePath);
    return [...esc, ...fu, ...nba]
      .filter((i) => !dismissedIds.has(i.id))
      .sort((a, b) => b.urgencyScore - a.urgencyScore);
  }, [escalations, followUps, nbaData, basePath, dismissedIds]);

  const filteredItems = useMemo(() => {
    switch (activeTab) {
      case 'urgent':
        return allItems.filter((i) => i.urgency === 'critical' || i.urgency === 'high');
      case 'followups':
        return allItems.filter((i) => i.type === 'followup');
      case 'opportunities':
        return allItems.filter((i) => i.type === 'opportunity');
      default:
        return allItems;
    }
  }, [allItems, activeTab]);

  const visibleItems = filteredItems.slice(0, MAX_VISIBLE);
  const hasMore = filteredItems.length > MAX_VISIBLE;
  const loading = isLoading || nbaLoading;

  const criticalCount = allItems.filter(
    (i) => i.urgency === 'critical' || i.urgency === 'high',
  ).length;

  const totalImpactCents = allItems.reduce((sum, i) => sum + (i.impactCents ?? 0), 0);
  const totalImpact = formatImpact(totalImpactCents);

  const toggleExpand = useCallback(
    (id: string) => setExpandedId((prev) => (prev === id ? null : id)),
    [],
  );

  /**
   * Run the plan from resolution-plan.ts, or throw. Removing a card is a claim
   * that the server agreed; every path here used to swallow its own failure, so
   * the card vanished either way and came back on the next load.
   *
   * ponytail: session-only for opportunities, because nothing can persist them
   * yet. A durable dismissal needs a STABLE recommendation key first — the
   * positional `-${i}` in rec.id shifts as ranking changes, so even a
   * dismissals table keyed on rec.id would leak the card back. Key on
   * (category, target entity) when that is built.
   */
  const persist = useCallback(async (item: FeedItem, kind: 'dismiss' | 'done'): Promise<'saved' | 'session'> => {
    const { attempts, sessionOnly } = planResolution(item.type, kind, { dealSignal: !!item.signalKey });
    if (sessionOnly) return 'session';

    let last: unknown;
    for (const attempt of attempts) {
      try {
        if (attempt === 'deal-signal-state') {
          // Same store as the Deal Coach: done hides the card until the deal's facts
          // change; dismiss snoozes it. ponytail: 7 days, the Coach's longest preset.
          await setSignalState(kind === 'done'
            ? { action: 'done', key: item.signalKey!, fingerprint: item.fingerprint ?? '' }
            : { action: 'snooze', key: item.signalKey!, days: 7 });
        }
        else if (attempt === 'reject-action') await rejectAction(item.rawId, 'Dismissed from feed');
        else await api(`/v1/escalations/${item.rawId}/dismiss`, { method: 'POST' });
        return 'saved';
      } catch (err) {
        last = err;   // try the next backing store; if none works, rethrow
      }
    }
    throw last instanceof Error ? last : new Error('no write succeeded');
  }, []);

  /**
   * Shared by Dismiss and Mark done. The card animates out immediately but is
   * only REMOVED once the write lands, so a failure brings it back instead of
   * leaving the user believing they handled something they did not.
   */
  const resolveItem = useCallback(async (item: FeedItem, kind: 'dismiss' | 'done', verb: string) => {
    setDismissingId(item.id);
    setExpandedId(null);
    try {
      const where = await persist(item, kind);
      setDismissedIds((prev) => new Set(prev).add(item.id));
      if (where === 'session') {
        toast('Hidden for now — recommendations are recomputed, so this returns on reload.', 'info');
      }
    } catch (err) {
      console.error(`[action-feed] ${verb} failed`, err);
      toast(`Could not ${verb} "${item.title}" — nothing was saved. Please try again.`, 'error');
    } finally {
      setDismissingId(null);
      queryClient.invalidateQueries({ queryKey: ['escalations'] });
      queryClient.invalidateQueries({ queryKey: ['follow-ups'] }); // the Command Center's Follow-Up Queue widget
      queryClient.invalidateQueries({ queryKey: ['next-best-actions'] });
      queryClient.invalidateQueries({ queryKey: SALES_TODAY_KEY });
    }
  }, [persist, queryClient, toast]);

  const dismissItem = useCallback(
    (item: FeedItem) => resolveItem(item, 'dismiss', 'dismiss'),
    [resolveItem],
  );

  const markDone = useCallback(
    (item: FeedItem) => resolveItem(item, 'done', 'mark done'),
    [resolveItem],
  );

  const tabCounts = useMemo(() => ({
    all: allItems.length,
    urgent: allItems.filter((i) => i.urgency === 'critical' || i.urgency === 'high').length,
    followups: allItems.filter((i) => i.type === 'followup').length,
    opportunities: allItems.filter((i) => i.type === 'opportunity').length,
  }), [allItems]);

  return (
    <DataCard
      title="Action Feed"
      subtitle={
        totalImpact && criticalCount > 0
          ? `${criticalCount} urgent · ${totalImpact} at stake`
          : criticalCount > 0
            ? `${criticalCount} urgent`
            : totalImpact
              ? `${totalImpact} in opportunities`
              : 'Prioritized across all signals'
      }
      badge={
        !loading && allItems.length > 0 ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-3xs font-semibold text-primary tabular-nums">
            {allItems.length}
          </span>
        ) : undefined
      }
    >
      {loading ? (
        <SkeletonList rows={5} />
      ) : isError && allItems.length === 0 ? (
        <QueryError error={new Error("Couldn't load your action feed")} onRetry={onRetry} compact />
      ) : allItems.length === 0 ? (
        <div className="flex items-center gap-2 py-4 justify-center">
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <p className="text-2xs text-ink-dim">
            All clear. No actions need attention.
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {/* Tab pills — compact, no wasted space */}
          <div className="flex gap-0.5 border-b border-border/30 pb-1">
            {TABS.map((tab) => {
              const count = tabCounts[tab.key];
              if (tab.key !== 'all' && count === 0) return null;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-2 py-0.5 text-2xs font-medium rounded-xs transition-colors ${
                    activeTab === tab.key
                      ? 'text-primary bg-primary/8'
                      : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  {tab.label}
                  {count > 0 && (
                    <span className="ml-1 text-3xs tabular-nums opacity-60">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Feed — tight rows like Linear/Bloomberg */}
          {visibleItems.length === 0 ? (
            <p className="text-2xs text-ink-dim py-3 text-center">
              No items match this filter.
            </p>
          ) : (
            <div className="divide-y divide-border/20">
              {visibleItems.map((item) => (
                <FeedRow
                  key={item.id}
                  item={item}
                  onNavigate={(path) => navigate(path)}
                  expanded={expandedId === item.id}
                  onToggleExpand={() => toggleExpand(item.id)}
                  onDismiss={() => dismissItem(item)}
                  onDone={() => markDone(item)}
                  dismissing={dismissingId === item.id}
                />
              ))}
            </div>
          )}

          {hasMore && (
            <button
              type="button"
              onClick={() => navigate(`${basePath}/actions`)}
              className="block w-full text-center text-2xs font-medium text-primary hover:text-primary/80 transition-colors py-1"
            >
              View all {allItems.length} &rarr;
            </button>
          )}
        </div>
      )}
    </DataCard>
  );
}
